import React, { useState, useEffect, useMemo } from 'react';
import {
  Database,
  Upload,
  Search,
  RefreshCw,
  CheckCircle,
  CheckCircle2,
  AlertCircle,
  AlertTriangle,
  X,
  FileText,
  Monitor,
  CreditCard,
  MapPin,
  Building2,
  Filter,
  ChevronUp,
  ChevronDown,
  ChevronsLeft,
  ChevronsRight,
  ChevronLeft,
  ChevronRight,
  Edit2,
  Eye,
  Settings,
  Wrench,
  MoreHorizontal,
  Download,
  LayoutGrid,
  Table2
} from 'lucide-react';

// URL de producción fija
const API_URL = window.location.hostname === 'localhost'
  ? 'http://localhost:3000'
  : 'https://chatmanantial.com';

// Definición de columnas
const COLUMNS = [
  { id: 'id', label: 'ID', width: '90px', sortable: true, filterable: true },
  { id: 'tipo', label: 'Tipo', width: '120px', sortable: true, filterable: true, filterType: 'select' },
  { id: 'nombre', label: 'Nombre', width: '200px', sortable: true, filterable: true },
  { id: 'provincia', label: 'Provincia', width: '120px', sortable: true, filterable: true },
  { id: 'ciudad', label: 'Ciudad', width: '120px', sortable: true, filterable: true },
  { id: 'teatro', label: 'Teatro', width: '180px', sortable: true, filterable: true },
  { id: 'inversor', label: 'Inversor', width: '150px', sortable: true, filterable: true },
  { id: 'lugar', label: 'Lugar', width: '180px', sortable: true, filterable: true },
  { id: 'puntoVenta', label: 'Punto de venta', width: '150px', sortable: true, filterable: true },
  { id: 'pinpad', label: 'Pinpad', width: '100px', sortable: true, filterable: true, filterType: 'select' },
  { id: 'version', label: 'Versión', width: '100px', sortable: true, filterable: true },
  { id: 'estado', label: 'Estado', width: '110px', sortable: true, filterable: true, filterType: 'select' },
  { id: 'acciones', label: 'Acciones', width: '100px', sortable: false, filterable: false }
];

// Tipos de aparatos con colores
const TIPOS_CONFIG = {
  'Taquilla': { color: 'bg-yellow-50', icon: Monitor, iconColor: 'text-yellow-600' },
  'Tenemosplan': { color: 'bg-blue-50', icon: Database, iconColor: 'text-blue-600' },
  'CRM': { color: 'bg-purple-50', icon: Building2, iconColor: 'text-purple-600' },
  'Maquina': { color: 'bg-green-50', icon: Settings, iconColor: 'text-green-600' },
  'default': { color: 'bg-white', icon: FileText, iconColor: 'text-gray-600' }
};

// Estados con iconos
const ESTADOS_CONFIG = {
  'estable': { icon: CheckCircle2, color: 'text-green-500', bg: 'bg-green-50', label: 'Estable' },
  'inestable': { icon: AlertTriangle, color: 'text-red-500', bg: 'bg-red-50', label: 'Inestable' },
  'mantenimiento': { icon: Wrench, color: 'text-yellow-500', bg: 'bg-yellow-50', label: 'Mant.' },
  'default': { icon: AlertCircle, color: 'text-gray-400', bg: 'bg-gray-50', label: '-' }
};

export default function AparatosManager({ user }) {
  const [stats, setStats] = useState(null);
  const [allData, setAllData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [importing, setImporting] = useState(false);
  const [message, setMessage] = useState(null);
  const [showImportModal, setShowImportModal] = useState(false);
  const [csvContent, setCsvContent] = useState('');

  // Estado de la tabla
  const [sortConfig, setSortConfig] = useState({ key: 'id', direction: 'desc' });
  const [columnFilters, setColumnFilters] = useState({});
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [viewMode, setViewMode] = useState('table'); // 'table' o 'cards'
  const [searchTerm, setSearchTerm] = useState('');

  // Cargar datos al montar
  useEffect(() => {
    loadStats();
    loadAllData();
  }, []);

  const loadStats = async () => {
    try {
      const response = await fetch(`${API_URL}/api/ai/aparatos/stats`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });
      if (response.ok) {
        const data = await response.json();
        setStats(data);
      }
    } catch (error) {
      console.error('Error cargando estadisticas:', error);
    }
  };

  const loadAllData = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/ai/aparatos/all`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });
      if (response.ok) {
        const data = await response.json();
        setAllData(data.aparatos || []);
      }
    } catch (error) {
      console.error('Error cargando datos:', error);
      // Datos de ejemplo si falla la API
      setAllData([
        { id: '10016940', tipo: 'Tenemosplan', nombre: 'REPASO BONO SEVILLA', provincia: 'SEVILLA', ciudad: 'SEVILLA', teatro: 'Bono Sevilla', inversor: 'MANANTIAL PR', lugar: 'SEVILLA', puntoVenta: 'WEB', pinpad: true, version: '2 (editable)', estado: 'estable' },
        { id: '10016907', tipo: 'Tenemosplan', nombre: 'WEB Acceso Móvil', provincia: 'HUEVA', ciudad: 'YEBRA DE BASA', teatro: 'MANANTIAL PR', inversor: 'MANANTIAL PR', lugar: 'WEB AYUNTAM', puntoVenta: 'WEB', pinpad: true, version: '2 (editable)', estado: 'estable' },
        { id: '10016876', tipo: 'Taquilla', nombre: 'TQ 2 AUDITORIO', provincia: 'VALLADOLID', ciudad: 'VALLADOLID', teatro: 'A LA CASA DE LA', inversor: 'MANANTIAL PR', lugar: 'Teatro Arroyo', puntoVenta: 'TQ ARROYO', pinpad: false, version: '-', estado: 'estable' },
        { id: '10016875', tipo: 'Taquilla', nombre: 'TQ 1 CASA CULTURA', provincia: 'VALLADOLID', ciudad: 'VALLADOLID', teatro: 'MANANTIAL PR', inversor: 'MANANTIAL PR', lugar: 'Teatro Arroyo', puntoVenta: 'TQ ARROYO', pinpad: false, version: '-', estado: 'estable' },
        { id: '10016811', tipo: 'Taquilla', nombre: 'TQ 2 PUERTA DELI', provincia: 'SEVILLA', ciudad: 'SEVILLA', teatro: 'REAL ALCÁZAR', inversor: 'MANANTIAL PR', lugar: 'REAL ALCÁZAR', puntoVenta: 'TQ REAL ALC', pinpad: true, version: '-', estado: 'inestable' },
        { id: '10016810', tipo: 'Taquilla', nombre: 'TQ 1 PUERTA DEL', provincia: 'SEVILLA', ciudad: 'SEVILLA', teatro: 'REAL ALCÁZAR', inversor: 'MANANTIAL PR', lugar: 'REAL ALCÁZAR', puntoVenta: 'TQ REAL ALC', pinpad: true, version: '-', estado: 'inestable' },
      ]);
    } finally {
      setLoading(false);
    }
  };

  // Obtener valores únicos para filtros tipo select
  const getUniqueValues = (key) => {
    const values = [...new Set(allData.map(item => {
      if (key === 'pinpad') return item[key] ? 'Sí' : 'No';
      return item[key] || '';
    }).filter(Boolean))];
    return values.sort();
  };

  // Filtrar y ordenar datos
  const filteredAndSortedData = useMemo(() => {
    let result = [...allData];

    // Aplicar búsqueda global (para vista tarjetas)
    if (searchTerm.trim()) {
      const search = searchTerm.toLowerCase();
      result = result.filter(item =>
        item.id?.toLowerCase().includes(search) ||
        item.nombre?.toLowerCase().includes(search) ||
        item.tipo?.toLowerCase().includes(search) ||
        item.ciudad?.toLowerCase().includes(search) ||
        item.teatro?.toLowerCase().includes(search) ||
        item.lugar?.toLowerCase().includes(search)
      );
    }

    // Aplicar filtros de columna
    Object.entries(columnFilters).forEach(([key, value]) => {
      if (value && value.trim()) {
        result = result.filter(item => {
          const itemValue = key === 'pinpad'
            ? (item[key] ? 'Sí' : 'No')
            : String(item[key] || '').toLowerCase();
          return itemValue.includes(value.toLowerCase());
        });
      }
    });

    // Aplicar ordenación
    if (sortConfig.key) {
      result.sort((a, b) => {
        let aVal = a[sortConfig.key];
        let bVal = b[sortConfig.key];

        // Manejo especial para pinpad (boolean)
        if (sortConfig.key === 'pinpad') {
          aVal = aVal ? 1 : 0;
          bVal = bVal ? 1 : 0;
        }

        if (aVal < bVal) return sortConfig.direction === 'asc' ? -1 : 1;
        if (aVal > bVal) return sortConfig.direction === 'asc' ? 1 : -1;
        return 0;
      });
    }

    return result;
  }, [allData, columnFilters, sortConfig, searchTerm]);

  // Paginación
  const totalPages = Math.ceil(filteredAndSortedData.length / rowsPerPage);
  const paginatedData = filteredAndSortedData.slice(
    (currentPage - 1) * rowsPerPage,
    currentPage * rowsPerPage
  );

  // Handlers
  const handleSort = (key) => {
    setSortConfig(prev => ({
      key,
      direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc'
    }));
  };

  const handleFilterChange = (key, value) => {
    setColumnFilters(prev => ({ ...prev, [key]: value }));
    setCurrentPage(1);
  };

  const clearFilters = () => {
    setColumnFilters({});
    setCurrentPage(1);
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (!file.name.endsWith('.csv')) {
      setMessage({ type: 'error', text: 'Solo se permiten archivos CSV' });
      return;
    }

    setImporting(true);
    setMessage(null);

    const formData = new FormData();
    formData.append('file', file);

    try {
      const response = await fetch(`${API_URL}/api/ai/import-aparatos`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` },
        body: formData
      });

      const data = await response.json();

      if (response.ok) {
        setMessage({ type: 'success', text: data.message });
        loadStats();
        loadAllData();
      } else {
        setMessage({ type: 'error', text: data.error || 'Error al importar' });
      }
    } catch (error) {
      console.error('Error:', error);
      setMessage({ type: 'error', text: 'Error de conexion al importar' });
    } finally {
      setImporting(false);
      e.target.value = '';
    }
  };

  const handleTextImport = async () => {
    if (!csvContent.trim()) {
      setMessage({ type: 'error', text: 'Pega el contenido CSV primero' });
      return;
    }

    setImporting(true);
    setMessage(null);

    try {
      const response = await fetch(`${API_URL}/api/ai/import-aparatos-text`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ csvContent })
      });

      const data = await response.json();

      if (response.ok) {
        setMessage({ type: 'success', text: data.message });
        loadStats();
        loadAllData();
        setShowImportModal(false);
        setCsvContent('');
      } else {
        setMessage({ type: 'error', text: data.error || 'Error al importar' });
      }
    } catch (error) {
      console.error('Error:', error);
      setMessage({ type: 'error', text: 'Error de conexion al importar' });
    } finally {
      setImporting(false);
    }
  };

  const getTipoConfig = (tipo) => TIPOS_CONFIG[tipo] || TIPOS_CONFIG.default;
  const getEstadoConfig = (estado) => ESTADOS_CONFIG[estado?.toLowerCase()] || ESTADOS_CONFIG.default;
  const canManage = user?.role === 'superadmin' || user?.role === 'admin';

  return (
    <div className="h-full flex flex-col bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 px-4 md:px-6 py-4">
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <Database className="w-8 h-8 text-[#3B82F6]" />
            <div>
              <h1 className="text-xl font-semibold text-gray-900">Listado de Aparatos</h1>
              <p className="text-sm text-gray-500">
                {filteredAndSortedData.length} de {allData.length} registros
              </p>
            </div>
          </div>

          <div className="flex items-center gap-2">
            {/* Toggle de vista */}
            <div className="flex items-center bg-gray-100 rounded-lg p-1">
              <button
                onClick={() => setViewMode('table')}
                className={`p-2 rounded-md transition-colors ${viewMode === 'table' ? 'bg-white shadow-sm text-[#3B82F6]' : 'text-gray-500 hover:text-gray-700'}`}
                title="Vista tabla"
              >
                <Table2 className="w-4 h-4" />
              </button>
              <button
                onClick={() => setViewMode('cards')}
                className={`p-2 rounded-md transition-colors ${viewMode === 'cards' ? 'bg-white shadow-sm text-[#3B82F6]' : 'text-gray-500 hover:text-gray-700'}`}
                title="Vista tarjetas"
              >
                <LayoutGrid className="w-4 h-4" />
              </button>
            </div>

            {/* Buscador global para vista tarjetas */}
            {viewMode === 'cards' && (
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <input
                  type="text"
                  placeholder="Buscar..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-9 pr-3 py-2 bg-gray-100 border-0 rounded-lg text-sm focus:ring-2 focus:ring-[#3B82F6] w-48"
                />
              </div>
            )}

            {canManage && (
              <>
                <label className="cursor-pointer">
                  <input
                    type="file"
                    accept=".csv"
                    onChange={handleFileUpload}
                    className="hidden"
                    disabled={importing}
                  />
                  <span className={`inline-flex items-center gap-2 px-3 py-2 rounded-lg text-sm ${
                    importing
                      ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}>
                    <Upload className="w-4 h-4" />
                    <span className="hidden sm:inline">Importar CSV</span>
                  </span>
                </label>

                <button
                  onClick={() => setShowImportModal(true)}
                  disabled={importing}
                  className="inline-flex items-center gap-2 px-3 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 text-sm"
                >
                  <FileText className="w-4 h-4" />
                  <span className="hidden sm:inline">Pegar</span>
                </button>
              </>
            )}

            <button
              onClick={() => { loadStats(); loadAllData(); }}
              className="inline-flex items-center gap-2 px-3 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 text-sm"
            >
              <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            </button>

            {Object.keys(columnFilters).length > 0 && (
              <button
                onClick={clearFilters}
                className="inline-flex items-center gap-2 px-3 py-2 bg-red-50 text-red-600 rounded-lg hover:bg-red-100 text-sm"
              >
                <X className="w-4 h-4" />
                Limpiar filtros
              </button>
            )}

            <button className="inline-flex items-center gap-2 px-4 py-2 bg-[#3B82F6] text-white rounded-lg hover:bg-[#2563EB] text-sm font-medium">
              <Wrench className="w-4 h-4" />
              <span className="hidden sm:inline">MANTENIMIENTO</span>
            </button>
          </div>
        </div>
      </div>

      {/* Mensaje de estado */}
      {message && (
        <div className={`mx-4 mt-4 p-3 rounded-lg flex items-center gap-2 ${
          message.type === 'success' ? 'bg-green-50 text-green-700 border border-green-200' :
          message.type === 'error' ? 'bg-red-50 text-red-700 border border-red-200' :
          'bg-blue-50 text-blue-700 border border-blue-200'
        }`}>
          {message.type === 'success' ? <CheckCircle className="w-5 h-5" /> : <AlertCircle className="w-5 h-5" />}
          <span>{message.text}</span>
          <button onClick={() => setMessage(null)} className="ml-auto">
            <X className="w-4 h-4" />
          </button>
        </div>
      )}

      {/* Contenido - Vista Tabla o Tarjetas */}
      <div className="flex-1 overflow-hidden p-4">
        {viewMode === 'table' ? (
          /* VISTA TABLA */
          <div className="bg-white rounded-lg border border-gray-200 h-full flex flex-col">
            {/* Tabla con scroll */}
            <div className="flex-1 overflow-auto">
              <table className="w-full border-collapse min-w-[1400px]">
              {/* Cabecera con ordenación */}
              <thead className="bg-gray-50 sticky top-0 z-10">
                <tr className="border-b border-gray-200">
                  {COLUMNS.map((col) => (
                    <th
                      key={col.id}
                      className="text-left text-xs font-semibold text-gray-600 uppercase tracking-wider px-3 py-2 border-r border-gray-200 last:border-r-0"
                      style={{ width: col.width, minWidth: col.width }}
                    >
                      {col.sortable ? (
                        <button
                          onClick={() => handleSort(col.id)}
                          className="flex items-center gap-1 hover:text-[#3B82F6] transition-colors w-full"
                        >
                          {col.label}
                          <span className="flex flex-col">
                            <ChevronUp className={`w-3 h-3 -mb-1 ${sortConfig.key === col.id && sortConfig.direction === 'asc' ? 'text-[#3B82F6]' : 'text-gray-300'}`} />
                            <ChevronDown className={`w-3 h-3 ${sortConfig.key === col.id && sortConfig.direction === 'desc' ? 'text-[#3B82F6]' : 'text-gray-300'}`} />
                          </span>
                        </button>
                      ) : (
                        col.label
                      )}
                    </th>
                  ))}
                </tr>

                {/* Fila de filtros */}
                <tr className="border-b border-gray-300 bg-gray-100">
                  {COLUMNS.map((col) => (
                    <td key={`filter-${col.id}`} className="px-1 py-1 border-r border-gray-200 last:border-r-0">
                      {col.filterable ? (
                        col.filterType === 'select' ? (
                          <select
                            value={columnFilters[col.id] || ''}
                            onChange={(e) => handleFilterChange(col.id, e.target.value)}
                            className="w-full px-2 py-1 text-xs border border-gray-300 rounded focus:outline-none focus:border-[#3B82F6] bg-white"
                          >
                            <option value="">Todos</option>
                            {getUniqueValues(col.id).map(val => (
                              <option key={val} value={val}>{val}</option>
                            ))}
                          </select>
                        ) : (
                          <input
                            type="text"
                            placeholder=""
                            value={columnFilters[col.id] || ''}
                            onChange={(e) => handleFilterChange(col.id, e.target.value)}
                            className="w-full px-2 py-1 text-xs border border-gray-300 rounded focus:outline-none focus:border-[#3B82F6]"
                          />
                        )
                      ) : (
                        <span></span>
                      )}
                    </td>
                  ))}
                </tr>
              </thead>

              {/* Cuerpo de la tabla */}
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan={COLUMNS.length} className="text-center py-12 text-gray-500">
                      <RefreshCw className="w-8 h-8 mx-auto mb-2 animate-spin text-[#3B82F6]" />
                      Cargando datos...
                    </td>
                  </tr>
                ) : paginatedData.length === 0 ? (
                  <tr>
                    <td colSpan={COLUMNS.length} className="text-center py-12 text-gray-400">
                      <Database className="w-12 h-12 mx-auto mb-2 opacity-50" />
                      No hay registros
                    </td>
                  </tr>
                ) : (
                  paginatedData.map((row, idx) => {
                    const tipoConfig = getTipoConfig(row.tipo);
                    const estadoConfig = getEstadoConfig(row.estado);
                    const TipoIcon = tipoConfig.icon;
                    const EstadoIcon = estadoConfig.icon;

                    return (
                      <tr
                        key={row.id || idx}
                        className={`border-b border-gray-100 hover:bg-blue-50/50 transition-colors ${tipoConfig.color}`}
                      >
                        <td className="px-3 py-2 text-sm font-mono text-gray-700 border-r border-gray-100">{row.id}</td>
                        <td className="px-3 py-2 border-r border-gray-100">
                          <span className="flex items-center gap-1.5 text-sm">
                            <TipoIcon className={`w-4 h-4 ${tipoConfig.iconColor}`} />
                            {row.tipo}
                          </span>
                        </td>
                        <td className="px-3 py-2 text-sm text-gray-900 border-r border-gray-100 truncate max-w-[200px]" title={row.nombre}>{row.nombre}</td>
                        <td className="px-3 py-2 text-sm text-gray-600 border-r border-gray-100">{row.provincia || '-'}</td>
                        <td className="px-3 py-2 text-sm text-gray-600 border-r border-gray-100">{row.ciudad || '-'}</td>
                        <td className="px-3 py-2 text-sm text-gray-600 border-r border-gray-100 truncate max-w-[180px]" title={row.teatro}>{row.teatro || '-'}</td>
                        <td className="px-3 py-2 text-sm text-gray-600 border-r border-gray-100">{row.inversor || '-'}</td>
                        <td className="px-3 py-2 text-sm text-gray-600 border-r border-gray-100 truncate max-w-[180px]" title={row.lugar}>{row.lugar || '-'}</td>
                        <td className="px-3 py-2 text-sm text-gray-600 border-r border-gray-100">{row.puntoVenta || '-'}</td>
                        <td className="px-3 py-2 border-r border-gray-100 text-center">
                          {row.pinpad ? (
                            <CheckCircle2 className="w-5 h-5 text-green-500 mx-auto" />
                          ) : (
                            <X className="w-5 h-5 text-red-400 mx-auto" />
                          )}
                        </td>
                        <td className="px-3 py-2 text-sm text-gray-600 border-r border-gray-100">{row.version || '-'}</td>
                        <td className="px-3 py-2 border-r border-gray-100">
                          <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium ${estadoConfig.bg} ${estadoConfig.color}`}>
                            <EstadoIcon className="w-3 h-3" />
                            {estadoConfig.label}
                          </span>
                        </td>
                        <td className="px-3 py-2">
                          <div className="flex items-center justify-center gap-1">
                            <button className="p-1 hover:bg-gray-100 rounded" title="Ver">
                              <Eye className="w-4 h-4 text-gray-500" />
                            </button>
                            <button className="p-1 hover:bg-gray-100 rounded" title="Editar">
                              <Edit2 className="w-4 h-4 text-blue-500" />
                            </button>
                            <button className="p-1 hover:bg-gray-100 rounded" title="Más">
                              <MoreHorizontal className="w-4 h-4 text-gray-500" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>

          {/* Paginación */}
          <div className="border-t border-gray-200 px-4 py-3 bg-gray-50 flex flex-col sm:flex-row items-center justify-between gap-3">
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setCurrentPage(1)}
                  disabled={currentPage === 1}
                  className="p-1 rounded hover:bg-gray-200 disabled:opacity-30 disabled:cursor-not-allowed"
                >
                  <ChevronsLeft className="w-5 h-5" />
                </button>
                <button
                  onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                  disabled={currentPage === 1}
                  className="p-1 rounded hover:bg-gray-200 disabled:opacity-30 disabled:cursor-not-allowed"
                >
                  <ChevronLeft className="w-5 h-5" />
                </button>
              </div>

              <span className="text-sm text-gray-600">
                Página{' '}
                <input
                  type="number"
                  min={1}
                  max={totalPages || 1}
                  value={currentPage}
                  onChange={(e) => {
                    const page = parseInt(e.target.value);
                    if (page >= 1 && page <= totalPages) setCurrentPage(page);
                  }}
                  className="w-12 px-2 py-1 border rounded text-center text-sm mx-1"
                />
                de {totalPages || 1}
              </span>

              <div className="flex items-center gap-2">
                <button
                  onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                  disabled={currentPage >= totalPages}
                  className="p-1 rounded hover:bg-gray-200 disabled:opacity-30 disabled:cursor-not-allowed"
                >
                  <ChevronRight className="w-5 h-5" />
                </button>
                <button
                  onClick={() => setCurrentPage(totalPages)}
                  disabled={currentPage >= totalPages}
                  className="p-1 rounded hover:bg-gray-200 disabled:opacity-30 disabled:cursor-not-allowed"
                >
                  <ChevronsRight className="w-5 h-5" />
                </button>
              </div>
            </div>

            <div className="flex items-center gap-3">
              <select
                value={rowsPerPage}
                onChange={(e) => {
                  setRowsPerPage(Number(e.target.value));
                  setCurrentPage(1);
                }}
                className="px-2 py-1 border rounded text-sm"
              >
                <option value={10}>10</option>
                <option value={25}>25</option>
                <option value={50}>50</option>
                <option value={100}>100</option>
              </select>
              <span className="text-sm text-gray-500">
                Mostrando {((currentPage - 1) * rowsPerPage) + 1}-{Math.min(currentPage * rowsPerPage, filteredAndSortedData.length)} de {filteredAndSortedData.length}
              </span>
            </div>
          </div>
        </div>
        ) : (
          /* VISTA TARJETAS */
          <div className="h-full overflow-auto">
            {loading ? (
              <div className="flex items-center justify-center h-64">
                <RefreshCw className="w-8 h-8 animate-spin text-[#3B82F6]" />
              </div>
            ) : paginatedData.length === 0 ? (
              <div className="flex flex-col items-center justify-center h-64 text-gray-400">
                <Database className="w-16 h-16 mb-3 opacity-50" />
                <p>No hay aparatos que coincidan con la búsqueda</p>
              </div>
            ) : (
              <>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                  {paginatedData.map((aparato, idx) => {
                    const tipoConfig = getTipoConfig(aparato.tipo);
                    const estadoConfig = getEstadoConfig(aparato.estado);
                    const TipoIcon = tipoConfig.icon;
                    const EstadoIcon = estadoConfig.icon;

                    return (
                      <div
                        key={aparato.id || idx}
                        className={`${tipoConfig.color} rounded-xl border border-gray-200 p-4 hover:shadow-lg transition-all cursor-pointer`}
                      >
                        {/* Header tarjeta */}
                        <div className="flex items-start justify-between mb-3">
                          <div className="flex items-center gap-2">
                            <div className={`p-2 rounded-lg ${tipoConfig.color === 'bg-white' ? 'bg-gray-100' : 'bg-white/60'}`}>
                              <TipoIcon className={`w-5 h-5 ${tipoConfig.iconColor}`} />
                            </div>
                            <div>
                              <span className="text-xs text-gray-500 font-mono">{aparato.id}</span>
                              <p className="font-medium text-gray-900 text-sm leading-tight">{aparato.nombre}</p>
                            </div>
                          </div>
                          <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-lg text-xs font-medium ${estadoConfig.bg} ${estadoConfig.color}`}>
                            <EstadoIcon className="w-3 h-3" />
                            {estadoConfig.label}
                          </span>
                        </div>

                        {/* Info tarjeta */}
                        <div className="space-y-2 text-sm">
                          <div className="flex items-center gap-2 text-gray-600">
                            <Building2 className="w-4 h-4 text-gray-400" />
                            <span className="truncate">{aparato.teatro || aparato.lugar || '-'}</span>
                          </div>
                          <div className="flex items-center gap-2 text-gray-600">
                            <MapPin className="w-4 h-4 text-gray-400" />
                            <span>{aparato.ciudad}{aparato.provincia ? `, ${aparato.provincia}` : ''}</span>
                          </div>
                          {aparato.puntoVenta && (
                            <div className="flex items-center gap-2 text-gray-600">
                              <CreditCard className="w-4 h-4 text-gray-400" />
                              <span>{aparato.puntoVenta}</span>
                            </div>
                          )}
                        </div>

                        {/* Footer tarjeta */}
                        <div className="flex items-center justify-between mt-4 pt-3 border-t border-gray-200">
                          <div className="flex items-center gap-2">
                            <span className="text-xs text-gray-500">{aparato.tipo}</span>
                            {aparato.pinpad && (
                              <span className="flex items-center gap-1 text-xs text-green-600 bg-green-50 px-2 py-0.5 rounded-full">
                                <CheckCircle2 className="w-3 h-3" />
                                Pinpad
                              </span>
                            )}
                          </div>
                          <div className="flex items-center gap-1">
                            <button className="p-1.5 hover:bg-white/80 rounded-lg transition-colors" title="Ver">
                              <Eye className="w-4 h-4 text-gray-500" />
                            </button>
                            <button className="p-1.5 hover:bg-white/80 rounded-lg transition-colors" title="Editar">
                              <Edit2 className="w-4 h-4 text-blue-500" />
                            </button>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>

                {/* Paginación para vista tarjetas */}
                <div className="mt-6 flex flex-col sm:flex-row items-center justify-between gap-4 bg-white rounded-xl p-4 border border-gray-200">
                  <div className="flex items-center gap-4">
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => setCurrentPage(1)}
                        disabled={currentPage === 1}
                        className="p-2 rounded-lg hover:bg-gray-100 disabled:opacity-30 disabled:cursor-not-allowed"
                      >
                        <ChevronsLeft className="w-5 h-5" />
                      </button>
                      <button
                        onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                        disabled={currentPage === 1}
                        className="p-2 rounded-lg hover:bg-gray-100 disabled:opacity-30 disabled:cursor-not-allowed"
                      >
                        <ChevronLeft className="w-5 h-5" />
                      </button>
                    </div>

                    <span className="text-sm text-gray-600">
                      Página <strong>{currentPage}</strong> de {totalPages || 1}
                    </span>

                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                        disabled={currentPage >= totalPages}
                        className="p-2 rounded-lg hover:bg-gray-100 disabled:opacity-30 disabled:cursor-not-allowed"
                      >
                        <ChevronRight className="w-5 h-5" />
                      </button>
                      <button
                        onClick={() => setCurrentPage(totalPages)}
                        disabled={currentPage >= totalPages}
                        className="p-2 rounded-lg hover:bg-gray-100 disabled:opacity-30 disabled:cursor-not-allowed"
                      >
                        <ChevronsRight className="w-5 h-5" />
                      </button>
                    </div>
                  </div>

                  <div className="flex items-center gap-3">
                    <select
                      value={rowsPerPage}
                      onChange={(e) => {
                        setRowsPerPage(Number(e.target.value));
                        setCurrentPage(1);
                      }}
                      className="px-3 py-2 border rounded-lg text-sm"
                    >
                      <option value={12}>12 por página</option>
                      <option value={24}>24 por página</option>
                      <option value={48}>48 por página</option>
                    </select>
                    <span className="text-sm text-gray-500">
                      {filteredAndSortedData.length} aparatos en total
                    </span>
                  </div>
                </div>
              </>
            )}
          </div>
        )}
      </div>

      {/* Modal de importación */}
      {showImportModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl max-w-2xl w-full max-h-[80vh] overflow-hidden">
            <div className="px-6 py-4 border-b flex items-center justify-between">
              <h3 className="font-semibold text-gray-900">Pegar contenido CSV</h3>
              <button onClick={() => { setShowImportModal(false); setCsvContent(''); }} className="text-gray-400 hover:text-gray-600">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="p-6">
              <p className="text-sm text-gray-600 mb-4">Copia y pega el contenido del archivo CSV incluyendo la cabecera.</p>
              <textarea
                value={csvContent}
                onChange={(e) => setCsvContent(e.target.value)}
                placeholder="id;Tipo;Nombre;Provincia;Ciudad;Teatro;Inversor;Cuidador;Lugar;Punto de venta;Pinpad..."
                className="w-full h-64 px-3 py-2 border rounded-lg font-mono text-sm focus:ring-2 focus:ring-[#3B82F6]"
              />
              <div className="mt-4 flex justify-end gap-3">
                <button onClick={() => { setShowImportModal(false); setCsvContent(''); }} className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg">
                  Cancelar
                </button>
                <button
                  onClick={handleTextImport}
                  disabled={importing || !csvContent.trim()}
                  className="px-4 py-2 bg-[#3B82F6] text-white rounded-lg hover:bg-[#2563EB] disabled:opacity-50"
                >
                  {importing ? 'Importando...' : 'Importar'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
